import React from 'react';
import { jsPDF } from 'jspdf';
import { PageHero } from '../components/layout/PageHero';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FileTextIcon, DownloadIcon, CalendarIcon } from 'lucide-react';
import { unsuccessfulBids, UnsuccessfulBidNotification } from '../data/procurement';
export function UnsuccessfulBids() {
  const handleDownload = (notification: UnsuccessfulBidNotification) => {
    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4'
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 20;
    // Letterhead band (AU green)
    doc.setFillColor(0, 104, 55);
    doc.rect(0, 0, pageWidth, 28, 'F');
    // Gold accent stripe
    doc.setFillColor(252, 209, 22);
    doc.rect(0, 28, pageWidth, 2, 'F');
    // AU emblem circle
    doc.setFillColor(255, 255, 255);
    doc.circle(marginX + 7, 14, 7, 'F');
    doc.setTextColor(0, 104, 55);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('AU', marginX + 7, 16, {
      align: 'center'
    });
    // Letterhead text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('AFRICAN UNION COMMISSION', marginX + 20, 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Procurement Department', marginX + 20, 19);
    doc.text('10 Carlton House Terrace, London SW1Y 5AA, United Kingdom', marginX + 20, 24);
    // Document title
    doc.setTextColor(26, 31, 46);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('NOTIFICATION OF UNSUCCESSFUL BID', pageWidth / 2, 48, {
      align: 'center'
    });
    // Divider
    doc.setDrawColor(252, 209, 22);
    doc.setLineWidth(0.8);
    doc.line(pageWidth / 2 - 25, 52, pageWidth / 2 + 25, 52);
    // Reference + date block
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    const refBlockY = 65;
    doc.text('Reference Number:', marginX, refBlockY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 31, 46);
    doc.text(notification.reference, marginX + 38, refBlockY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('Date of Issue:', pageWidth - marginX - 50, refBlockY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 31, 46);
    const formattedDate = new Date(notification.date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    doc.text(formattedDate, pageWidth - marginX - 25, refBlockY);
    // Subject
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(10);
    doc.text('Subject:', marginX, refBlockY + 10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(26, 31, 46);
    doc.setFontSize(11);
    const subjectLines = doc.splitTextToSize(notification.title, pageWidth - marginX * 2 - 20);
    doc.text(subjectLines, marginX + 20, refBlockY + 10);
    // Body
    let bodyY = refBlockY + 10 + subjectLines.length * 6 + 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(26, 31, 46);
    doc.text('Dear Bidder,', marginX, bodyY);
    bodyY += 8;
    const paragraphs = ['Thank you for your participation in the African Union Commission procurement process referenced above.', 'Following a comprehensive and impartial evaluation of all submissions in accordance with the published evaluation criteria, we regret to inform you that your bid was not successful on this occasion.', 'The contract has been awarded to the bidder whose proposal was determined to be the most economically advantageous and most responsive to the requirements set out in the tender documents.', 'We acknowledge and appreciate the time, effort, and resources you invested in preparing your submission. We encourage you to continue participating in future procurement opportunities published on our Bids & Tenders portal.', 'Should you wish to request a debriefing on your submission, please contact our Procurement Department within ten (10) working days of the date of this notification, using the contact details provided below.'];
    paragraphs.forEach(p => {
      const lines = doc.splitTextToSize(p, pageWidth - marginX * 2);
      doc.text(lines, marginX, bodyY);
      bodyY += lines.length * 5.5 + 4;
    });
    // Sign-off
    bodyY += 4;
    doc.text('Yours sincerely,', marginX, bodyY);
    bodyY += 14;
    doc.setFont('helvetica', 'bold');
    doc.text('Head of Procurement', marginX, bodyY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('African Union Commission', marginX, bodyY + 5);
    // Footer band
    const footerY = pageHeight - 22;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.3);
    doc.line(marginX, footerY, pageWidth - marginX, footerY);
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text('Procurement Department  |  procurement@aucommission.org  |  +44 (0) 20 4571 0123', pageWidth / 2, footerY + 5, {
      align: 'center'
    });
    doc.text('African Union Commission, London Office  |  www.africanunion.org', pageWidth / 2, footerY + 10, {
      align: 'center'
    });
    doc.setTextColor(150, 150, 150);
    doc.text(`This is an officially issued notification document — Ref: ${notification.reference}`, pageWidth / 2, footerY + 15, {
      align: 'center'
    });
    doc.save(`${notification.reference}-unsuccessful-bid-notification.pdf`);
  };
  return <div data-id="element-1199">
      <PageHero title="Notification of Unsuccessful Bids" subtitle="Transparency in procurement outcomes and feedback to bidders" breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'Procurement'
    }, {
      label: 'Unsuccessful Bids'
    }]} data-id="element-1200" />

      <div className="max-w-7xl mx-auto px-4 py-12" data-id="element-1201">
        <div className="bg-au-green-light rounded-lg p-6 mb-8" data-id="element-1202">
          <h2 className="text-xl font-bold text-charcoal mb-2" data-id="element-1203">
            About Unsuccessful Bid Notifications
          </h2>
          <p className="text-gray-700" data-id="element-1204">
            In line with our commitment to transparency and fairness, we publish
            notifications for unsuccessful bids. These documents provide
            feedback to bidders and maintain accountability in our procurement
            processes.
          </p>
        </div>

        <div className="space-y-4" data-id="element-1205">
          {unsuccessfulBids.map(notification => <Card key={notification.id} hover data-id="element-1206">
              <div className="flex items-start gap-4" data-id="element-1207">
                <div className="w-12 h-12 bg-au-green-light rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1208">
                  <FileTextIcon size={24} className="text-au-green" data-id="element-1209" />
                </div>
                <div className="flex-1" data-id="element-1210">
                  <div className="flex items-start justify-between gap-4 mb-2" data-id="element-1211">
                    <div data-id="element-1212">
                      <span className="text-sm font-mono text-gray-500 block mb-1" data-id="element-1213">
                        {notification.reference}
                      </span>
                      <h3 className="text-lg font-bold text-charcoal" data-id="element-1214">
                        {notification.title}
                      </h3>
                    </div>
                    <Button variant="secondary" onClick={() => handleDownload(notification)} className="flex items-center gap-2 text-sm flex-shrink-0" data-id="element-1215">
                      <DownloadIcon size={16} data-id="element-1216" />
                      Download PDF
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1217">
                    <CalendarIcon size={14} data-id="element-1218" />
                    <span data-id="element-1219">
                      Published:{' '}
                      {new Date(notification.date).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                </div>
              </div>
            </Card>)}
        </div>

        <div className="mt-12 bg-white rounded-lg border border-gray-border p-8" data-id="element-1220">
          <h3 className="text-xl font-bold text-charcoal mb-4" data-id="element-1221">
            Questions About Procurement Outcomes?
          </h3>
          <p className="text-gray-700 mb-6" data-id="element-1222">
            If you have questions about a specific procurement outcome or would
            like to request feedback on your submission, please contact our
            procurement team.
          </p>
          <div className="flex flex-wrap gap-4" data-id="element-1223">
            <a href="mailto:procurement@aucommission.org" className="text-au-green font-medium hover:underline" data-id="element-1224">
              procurement@aucommission.org
            </a>
            <span className="text-gray-400" data-id="element-1225">|</span>
            <span className="text-gray-700" data-id="element-1226">
              +44 (0) 20 4571 0123{' '}
              <span className="text-gray-500" data-id="element-1227">(Procurement)</span>
            </span>
          </div>
        </div>
      </div>
    </div>;
}